/**
 * Runtime configuration for the backend.
 * Only PUBLIC values belong here: the Supabase URL and anon key are designed
 * to be public (Row Level Security protects data). SumUp keys, the Supabase
 * service-role key and any other secret live ONLY in Edge Function secrets.
 */
export const backend = {
  url: (process.env.REACT_APP_SUPABASE_URL || "").replace(/\/$/, ""),
  anonKey: process.env.REACT_APP_SUPABASE_ANON_KEY || "",
};

export const isBackendConfigured = () => Boolean(backend.url && backend.anonKey);

/** The in-browser simulated checkout exists only on the local dev server. */
export const isDevSimulation = () =>
  process.env.NODE_ENV === "development" && !isBackendConfigured();

/** Legacy anon keys are JWTs ("eyJ..."); new publishable keys ("sb_publishable_...") are not. */
export const isLegacyJwtKey = (key) => /^eyJ[\w-]+\.[\w-]+\.[\w-]+$/.test(key || "");

/**
 * Public request headers. The key always goes in `apikey`. Only a legacy JWT
 * anon key may also be sent as a Bearer token; a publishable key must not be.
 */
export const publicHeaders = () => ({
  apikey: backend.anonKey,
  ...(isLegacyJwtKey(backend.anonKey) ? { Authorization: `Bearer ${backend.anonKey}` } : {}),
});

const headers = () => ({ ...publicHeaders(), "Content-Type": "application/json" });

/** Calls a public Postgres function (RPC) through Supabase's REST API. */
export async function callRpc(name, args = {}) {
  const res = await fetch(`${backend.url}/rest/v1/rpc/${name}`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(args),
  });
  if (!res.ok) throw new Error(`rpc ${name} failed (${res.status})`);
  return res.json();
}

/** Calls a Supabase Edge Function. Returns { ok, status, data }. */
export async function callFunction(name, body) {
  const res = await fetch(`${backend.url}/functions/v1/${name}`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(body),
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  return { ok: res.ok, status: res.status, data };
}
